// テーマのイベントシステムからイベント名の定数をインポート
import {
  ThemeEvents
} from '@theme/events';

/**
 * 追従カートのカスタム要素
 * 既存のvariant-pickerと同期してバリエーション選択を行う
 */
class StickyCart extends HTMLElement {
  // 要素がDOMに追加された時に呼ばれる
  connectedCallback() {
    // 最も近い.shopify-sectionを取得（イベントのバブリング先）
    const closestSection = this.closest('.shopify-section');
    if (!closestSection) return;

    // VariantUpdateEventをリッスン開始（既存のvariant-pickerからの通知を受け取る）
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    // 追従カート内のセレクトボックスの変更をリッスン
    this.querySelectorAll('.sticky-cart__select').forEach((select) => {
      select.addEventListener('change', this.handleSelectChange);
    });

    // トリガーボタンのクリックイベント
    const triggerButton = this.querySelector('[ref="stickyCartTrigger"]');
    if (triggerButton) {
      triggerButton.addEventListener('click', this.handleTriggerClick);
    }

    // 閉じるボタンのクリックイベント
    const closeButton = this.querySelector('[ref="stickyCartClose"]');
    if (closeButton) {
      closeButton.addEventListener('click', this.handleCloseClick);
    }

    // メインのカートボタンを監視してトリガーボタンの表示/非表示を制御
    this.setupIntersectionObserver();
  }

  // 要素がDOMから削除された時に呼ばれる
  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section');
    if (!closestSection) return;

    // メモリリーク防止のためイベントリスナーを解除
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    this.querySelectorAll('.sticky-cart__select').forEach((select) => {
      select.removeEventListener('change', this.handleSelectChange);
    });

    const triggerButton = this.querySelector('[ref="stickyCartTrigger"]');
    if (triggerButton) {
      triggerButton.removeEventListener('click', this.handleTriggerClick);
    }

    const closeButton = this.querySelector('[ref="stickyCartClose"]');
    if (closeButton) {
      closeButton.removeEventListener('click', this.handleCloseClick);
    }

    // スクロールイベントリスナーを解除
    //observerをクリーンアップ
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * メインのカートボタンを監視するスクロールイベントをセットアップ
   */
  setupIntersectionObserver() {
    this.mainAddToCartButton = document.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
    if (!this.mainAddToCartButton) return;

    const hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';

    this.observer = new IntersectionObserver((entries) => {
      const triggerButton = this.querySelector('[ref="stickyCartTrigger"]');
      const panel = this.querySelector('[ref="stickyCartPanel"]');

      entries.forEach(entry => {
        // 単一バリエーションの場合はパネルの表示/非表示を制御
        if (hasOnlyDefaultVariant) {
          if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
            panel?.classList.add('is-open');
          } else {
            panel?.classList.remove('is-open');
          }
          return;
        }

        // 複数バリエーションの場合はトリガーボタンの表示/非表示を制御
        if (panel?.classList.contains('is-open')) return;

        // 画面外 かつ 上方向に出た場合のみ表示
        if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
          triggerButton?.classList.add('is-visible');
        } else {
          triggerButton?.classList.remove('is-visible');
        }
      });
    });

    this.observer.observe(this.mainAddToCartButton);
  }


  /**
   * トリガーボタンがクリックされた時の処理
   */
  handleTriggerClick = () => {
    const panel = this.querySelector('[ref="stickyCartPanel"]');
    const triggerButton = this.querySelector('[ref="stickyCartTrigger"]');

    if (panel) {
      panel.classList.add('is-open');
    }
    if (triggerButton) {
      triggerButton.classList.remove('is-visible');
    }
  };

  /**
   * 閉じるボタンがクリックされた時の処理
   */
  handleCloseClick = () => {
    const panel = this.querySelector('[ref="stickyCartPanel"]');
    const triggerButton = this.querySelector('[ref="stickyCartTrigger"]');

    if (panel) {
      panel.classList.remove('is-open');
    }

    // メインボタンが見えない場合はトリガーボタンを再表示
    const mainAddToCartButton = document.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
    if (mainAddToCartButton && triggerButton) {
      const rect = mainAddToCartButton.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        triggerButton.classList.add('is-visible');
      }
    }
  };

  /**
   * 既存のvariant-pickerからのVariantUpdateEventを受け取って追従カートを更新
   * @param {Event} event - バリエーション更新イベント
   */
  handleVariantUpdate = (event) => {
    // Combined Listing（複数商品を1ページで切り替え）の場合、商品IDを更新
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }
    // 商品IDが一致しない場合は更新をスキップ（別の商品のイベントを無視）
    else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Section Rendering APIから返されたHTMLから新しい追従カートを取得
    const newStickyCart = event.detail.data.html.querySelector('sticky-cart [ref="stickyCartContainer"]');
    const currentStickyCart = this.querySelector('[ref="stickyCartContainer"]');

    if (!newStickyCart || !currentStickyCart) return;

    // 常にDOM更新
    currentStickyCart.replaceWith(newStickyCart);

    // 新しい要素にイベントリスナーを再登録
    this.querySelectorAll('.sticky-cart__select').forEach((select) => {
      select.addEventListener('change', this.handleSelectChange);
    });

    // トリガーボタンのテキストも更新
    const newTriggerText = event.detail.data.html.querySelector('sticky-cart [ref="stickyCartTriggerText"]');
    const currentTriggerText = this.querySelector('[ref="stickyCartTriggerText"]');
    if (newTriggerText && currentTriggerText) {
      currentTriggerText.textContent = newTriggerText.textContent;
    }
  };

  /**
   * 追従カート内のセレクトボックスが変更された時の処理
   * 既存のvariant-pickerを更新する
   * @param {Event} event - changeイベント
   */
  handleSelectChange = (event) => {
    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];
    const optionValueId = selectedOption.dataset.optionValueId;

    if (!optionValueId) return;

    // 既存のvariant-pickerを探す
    const mainVariantPicker = document.querySelector('variant-picker[ref="mainVariantPicker"]');
    if (!mainVariantPicker) return;

    // variant-picker内の対応する要素を探す
    const targetElement = mainVariantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!targetElement) return;

    // ラジオボタンの場合
    if (targetElement instanceof HTMLInputElement && targetElement.type === 'radio') {
      targetElement.checked = true;
      // changeイベントを発火してvariant-pickerの処理をトリガー
      targetElement.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    }
    // セレクトボックスの場合
    else if (targetElement instanceof HTMLOptionElement) {
      const parentSelect = targetElement.closest('select');
      if (parentSelect) {
        parentSelect.value = targetElement.value;
        // changeイベントを発火してvariant-pickerの処理をトリガー
        parentSelect.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }
    }
  };
}

// カスタム要素を登録（重複登録を防ぐ）
if (!customElements.get('sticky-cart')) {
  customElements.define('sticky-cart', StickyCart);
}